/**
 * CartIcon.jsx — Icône panier pour le Header avec badge compteur + CartDrawer intégré.
 *
 * §11/§18 — Composant primitif à placer dans le Header.
 * §20 — Tokens uniquement depuis theme.js.
 * §12 ter — Zéro px en dur pour espacements/polices.
 * §12 undecies — Casse grammaticale.
 * §12 terdecies — Aucun composant natif navigateur.
 */
import React from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { openCart, selectCartCount } from '../../../store/slices/cartSlice';
import {COLORS, FONTS, RADII, FONT_SIZE, FONT_WEIGHT} from '../theme';
import { CartDrawer } from './CartDrawer';

/* ─── Styled components ─────────────────────────────────────────────────────── */

const IconWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const IconBtn = styled.button`
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${RADII.circle};
  border: none;
  background: transparent;
  color: ${COLORS.ink};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.14s;
  padding: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.92);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -0.15rem;
  right: -0.15rem;
  min-width: 1.1rem;
  height: 1.1rem;
  border-radius: ${RADII.pill};
  background: ${COLORS.accent};
  color: ${COLORS.white};
  font-family: ${FONTS.head};
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.extrabold};
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
  pointer-events: none;
  border: 0.125rem solid transparent; /* visual breathing room */
`;

/* ─── SVG Cart Icon ─────────────────────────────────────────────────────────── */

const CartSvg = () => (
  <svg
    width="1.35rem"
    height="1.35rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

/* ─── Component ─────────────────────────────────────────────────────────────── */

/**
 * CartIcon — Icône panier Header.
 *
 * Rend le CartDrawer inline ; passe `onCheckout` au drawer.
 *
 * @param {Function} [onCheckout] - callback passé au CartDrawer "Proceed to Checkout"
 */
export function CartIcon({ onCheckout }) {
  const dispatch = useDispatch();
  const count = useSelector(selectCartCount);

  const handleOpen = () => dispatch(openCart());

  return (
    <IconWrap>
      <IconBtn
        type="button"
        data-testid="cart-icon"
        aria-label={`Open cart${count > 0 ? `, ${count} item${count === 1 ? '' : 's'}` : ''}`}
        onClick={handleOpen}
      >
        <CartSvg />
        {count > 0 && (
          <Badge aria-hidden="true">{count > 99 ? '99+' : count}</Badge>
        )}
      </IconBtn>

      {/* CartDrawer rendered here — self-manages its open/close state via Redux */}
      <CartDrawer onCheckout={onCheckout} />
    </IconWrap>
  );
}

export default CartIcon;
